import {
  BlockWrapper,
  defineBlock,
  smartProps,
  useEditor,
  useInspectorControl,
} from "@smartfire/js-block-engine";
import apiFetch from "@wordpress/api-fetch";
import { lazy, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import he from "he";
import moment from "moment/moment";
import { displayFormat, readingTime, stripTags } from "../shared/tools.js";
import style from "./block.module.scss";

const Inspector = lazy(() => import("./inspector.js"));

export const PostInsert = ({ postId, post }) => {
  useInspectorControl(Inspector);
  const { isEditing } = useEditor();
  const [displayedPost, setDisplayedPost] = useState(post);
  const TagName = isEditing ? "div" : "a";
  // React i18next
  const { t } = useTranslation();

  const parseRestData = (post) => {
    return {
      id: post.id,
      title: post.title?.rendered,
      categories: post.categories_data,
      author: post?.author_data.data.display_name,
      link: post.link,
      thumbnail: {
        title:
          post._embedded["wp:featuredmedia"] &&
          post._embedded["wp:featuredmedia"][0].title.rendered,
        url:
          post._embedded["wp:featuredmedia"] &&
          post._embedded["wp:featuredmedia"][0].source_url,
        alt:
          post._embedded["wp:featuredmedia"] &&
          post._embedded["wp:featuredmedia"][0].alt_text,
      },
      date: post.date,
      excerpt: post.excerpt.rendered,
      reading_time: readingTime(post.content.rendered),
    };
  };

  // Display post in BO
  useEffect(() => {
    if (!isEditing || !postId) return;

    apiFetch({
      path: `/wp/v2/posts/${postId}/?_embed`,
    }).then((res) => {
      setDisplayedPost(parseRestData(res));
    });
  }, [isEditing, postId]);

  return displayedPost ? (
    <BlockWrapper.Div className={style.block}>
      <TagName className={style.container} href={displayedPost.link}>
        <div className={style.thumbnailWrapper}>
          {displayedPost.thumbnail?.url && (
            <img
              src={displayedPost.thumbnail.url}
              alt=""
              loading="lazy"
              aria-hidden="true"
            />
          )}
        </div>

        <div className={style.content}>
          <div className={style.description}>
            {displayedPost?.title && (
              <div className={style.title}>
                {he.decode(stripTags(displayedPost.title))}
              </div>
            )}

            {displayedPost?.excerpt && (
              <div className={style.excerpt}>
                {he.decode(stripTags(displayedPost.excerpt))}
              </div>
            )}
          </div>

          <div className={style.authorDate}>
            <span className={style.author}>{displayedPost?.author}</span>

            <span className={style.date}>
              {t("on") +
                " " +
                moment(displayedPost.date).format(displayFormat())}
            </span>
          </div>
        </div>
      </TagName>

      {displayedPost.categories.length > 0 && (
        <a className={style.category} href={displayedPost.categories[0].link}>
          {displayedPost.categories[0]?.name
            ? he.decode(stripTags(displayedPost.categories[0].name))
            : ""}
        </a>
      )}
    </BlockWrapper.Div>
  ) : (
    <div className={style.skeleton}>
      <div className={style.imgSkeleton} />
      <div className={style.contentSkeleton} />
      <div className={style.categorySkeleton} />
    </div>
  );
};

export default defineBlock({
  Block: PostInsert,
  setup: {
    title: "Post Insert",
  },
  smartProps: {
    postId: smartProps.number.withDefault(0),
    categoryId: smartProps.number.withDefault(0),
  },
});
